import { type AriaToolbarProps, useToolbar } from '@react-aria/toolbar';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';
import React from 'react';

import { assert } from '@gitbook/util-ts';

import { fadeStaggerChildren } from '../animations';
import { checkIsTouchDevice } from '../capabilities';

type RevealableToolbarState = {
    containerProps: {
        onFocus: () => void;
        onBlur: () => void;
        onHoverStart: () => void;
        onHoverEnd: () => void;
    } | null;
    visible: boolean;
};

const isTouchDevice = checkIsTouchDevice();

function useRevealableToolbarState() {
    const [visible, setVisible] = React.useState(false);
    return React.useMemo(
        () => ({
            containerProps: isTouchDevice
                ? null
                : {
                      onFocus: () => setVisible(true),
                      onBlur: () => setVisible(false),
                      onHoverStart: () => setVisible(true),
                      onHoverEnd: () => setVisible(false),
                  },
            visible: isTouchDevice || visible,
        }),
        [visible]
    );
}

const RevealableToolbarContext = React.createContext<RevealableToolbarState | null>(null);

function useRevealableToolbarContext() {
    const context = React.useContext(RevealableToolbarContext);
    assert(context, 'RevealableToolbarContext is not provided');
    return context;
}

export type RevealableToolbarRootProps = {
    className?: string;
    children: React.ReactNode;
    disabled?: boolean;
};

export function RevealableToolbarRoot(props: RevealableToolbarRootProps) {
    const state = useRevealableToolbarState();

    return (
        <RevealableToolbarContext.Provider value={state}>
            <motion.div
                {...state.containerProps}
                className={clsx('flex items-center gap-2', props.className)}
            >
                {props.children}
            </motion.div>
        </RevealableToolbarContext.Provider>
    );
}

export type RevealableToolbarProps = AriaToolbarProps & {
    className?: string;
    children: React.ReactNode;
    /** When to show the revealable element */
    when?: 'always' | 'hover' | 'never';
};

export function RevealableToolbar(props: RevealableToolbarProps) {
    const state = useRevealableToolbarContext();
    const ref = React.useRef<HTMLDivElement>(null);
    const { when = 'hover', ...rest } = props;
    const {
        toolbarProps: { onAnimationStart, onDragStart, onDragEnd, onDrag, ...toolbarProps },
    } = useToolbar(rest, ref);

    if (when === 'never') {
        return null;
    }

    if (when === 'always') {
        return (
            <div className={clsx('flex shrink-0 items-center gap-1', props.className)}>
                {props.children}
            </div>
        );
    }

    return (
        <motion.div
            ref={ref}
            className={clsx('flex shrink-0 items-center gap-1', props.className)}
            variants={fadeStaggerChildren}
            initial="hidden"
            animate={state.visible ? 'show' : 'hidden'}
            {...toolbarProps}
        >
            {props.children}
        </motion.div>
    );
}

export type RevealableToolbarItemProps = { children: React.ReactNode; className?: string };

export function RevealableToolbarItem(props: RevealableToolbarItemProps) {
    const reducedMotion = useReducedMotion();
    return (
        <motion.div
            className={clsx('flex', props.className)}
            variants={{
                hidden: { width: 0, opacity: 0, marginRight: -15, pointerEvents: 'none' },
                show: { width: 'auto', opacity: 1, marginRight: 0, pointerEvents: 'auto' },
            }}
            transition={{ duration: reducedMotion ? 0 : 0.2 }}
        >
            {props.children}
        </motion.div>
    );
}
